import { 
    collection, 
    doc, 
    setDoc, 
    addDoc, 
    serverTimestamp,
    writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';
import { trackEvent, getPatternFeatures, getTimeSignaturesUsed } from './analytics';

// Check if pattern data exceeds Firestore 1MB limit
function exceedsFirestoreLimit(data) {
  const dataSize = new Blob([JSON.stringify(data)]).size;
  return dataSize > 1048576; // 1MB in bytes
}

// Split large pattern into chunks for storage
function splitPatternForStorage(patternData) {
  if (!exceedsFirestoreLimit(patternData)) {
    return {
      mainDoc: patternData,
      chunks: [],
      needsChunking: false
    };
  }

  console.log('Pattern exceeds 1MB, splitting into chunks...');
  
  // Separate measures from main document
  const { measures, ...mainDoc } = patternData;
  
  // Calculate optimal chunk size (aim for ~800KB per chunk to stay under 1MB)
  const chunkSize = 50; // measures per chunk - adjust based on complexity
  const chunks = [];
  
  if (measures && Array.isArray(measures)) {
    for (let i = 0; i < measures.length; i += chunkSize) {
      chunks.push({
        chunkIndex: Math.floor(i / chunkSize),
        measures: measures.slice(i, i + chunkSize),
        startMeasure: i,
        endMeasure: Math.min(i + chunkSize - 1, measures.length - 1)
      });
    }
  }

  // Enhanced main document with chunking metadata
  const enhancedMainDoc = {
    ...mainDoc,
    isChunkedPattern: true,
    totalMeasures: measures?.length || 0,
    chunkCount: chunks.length,
    chunkSize: chunkSize,
    originalSize: new Blob([JSON.stringify(patternData)]).size
  };

  console.log(`Split pattern into ${chunks.length} chunks (${measures?.length || 0} measures)`);
  
  return {
    mainDoc: enhancedMainDoc,
    chunks,
    needsChunking: true
  };
}

/**
 * Manual save function that handles saving patterns with user confirmation
 * @param {Object} params - All the parameters needed for saving
 * @returns {Promise} - Resolves when save is complete
 */
export async function handleManualSave({
    // User and authentication
    currentUser,
    
    // Pattern data
    patternName,
    setPatternName,
    folderName,
    tempo,
    useTempoChanges,
    enableAdditionalTempoChanges,
    tempoChanges,
    aTuning,
    muteRandomBeats,
    mutePercentage,
    loopMode,
    measures,
    startMeasureNumber,
    stressOn,
    selectedNoteValue,
    modulateOnLoop,
    modLoopInterval,
    modLoopDirection,
    rehearsalLetters,
    timeSignatureColors,
    
    // App state
    savedPatterns,
    setSavedPatterns,
    subscriptionTier,
    patternCache,
    
    // Dependencies
    removeUndefinedDeep,
  }) {
    if (!currentUser) {
      alert("Please log in to save patterns.");
      return;
    }
  
    // Check if pattern name exists
    let actualPatternName = patternName.trim();
    
    if (!actualPatternName) {
      const userInput = window.prompt(
        "🎵 Give your pattern a name to save it!\n\nThis will help you find it later in your collection:",
        "My Pattern"
      );
      
      if (userInput && userInput.trim()) {
        actualPatternName = userInput.trim();
        setPatternName(actualPatternName);
      } else {
        return; // User cancelled
      }
    }
  
    const folderTrimmed = folderName.trim() || "Uncategorized";
  
    console.log('🔍 MANUAL SAVE DEBUG - Starting save for:', actualPatternName, folderTrimmed);
  
    // Build the pattern object
    const newPattern = {
      updatedAt: serverTimestamp(),
      name: actualPatternName,
      folder: folderTrimmed,
      tempo,
      useTempoChanges: !!useTempoChanges,
      enableAdditionalTempoChanges: !!enableAdditionalTempoChanges,
      tempoChanges,
      aTuning,
      muteRandomBeats,
      mutePercentage,
      loopMode,
      measures,
      startMeasureNumber,
      stressOn,
      selectedNoteValue,
      modulateOnLoop: !!modulateOnLoop,
      modLoopInterval,
      modLoopDirection,
      rehearsalLetters,
      timeSignatureColors,
    };
  
    const cleanedData = removeUndefinedDeep(newPattern);
  
    // Check for existing pattern
    const existingPatternIndex = savedPatterns.findIndex(
      (p) => p.name === actualPatternName && p.folder === folderTrimmed
    );
    const existingPattern = existingPatternIndex !== -1 ? savedPatterns[existingPatternIndex] : null;
  
    const patternsRef = collection(db, "users", currentUser.uid, "patterns");
  
    try {
      if (existingPattern) {
        // Update existing pattern with confirmation
        const overwriteConfirmed = window.confirm(
          `A pattern named "${actualPatternName}" already exists in ` +
            `folder "${folderTrimmed}". Do you want to overwrite it?`
        );
        if (!overwriteConfirmed) {
          return;
        }
  
        console.log('🔍 MANUAL SAVE DEBUG - Updating existing pattern with ID:', existingPattern.id);
  
        const docRef = doc(db, "users", currentUser.uid, "patterns", existingPattern.id);
        await setDoc(docRef, cleanedData, { merge: true });
  
        // Update cache
        const cacheKey = `${actualPatternName}-${folderTrimmed}`;
        patternCache.current.set(cacheKey, measures);
  
        // Update local state
        setSavedPatterns(prev => {
          const updated = [...prev];
          updated[existingPatternIndex] = {
            ...existingPattern,
            ...cleanedData,
            id: existingPattern.id,
            updatedAt: new Date()
          };
          return updated;
        });
  
        alert(`Pattern "${actualPatternName}" in folder "${folderTrimmed}" updated!`);
      } else {
        // Create new pattern
        console.log('🔍 MANUAL SAVE DEBUG - Creating new pattern');
  
        // Check pattern limits
        let allowedCount;
        if (subscriptionTier === "pro") allowedCount = Infinity;
        else if (subscriptionTier === "basic") allowedCount = 10;
        else allowedCount = 3;
  
        if (savedPatterns.length >= allowedCount) {
          alert(`Pattern limit reached (${allowedCount}). Please upgrade or delete patterns.`);
          return;
        }
  
        const newPatternData = {
          ...cleanedData,
          createdAt: serverTimestamp()
        };
  
        const docRef = await addDoc(patternsRef, newPatternData);
  
        // Update cache
        const cacheKey = `${actualPatternName}-${folderTrimmed}`;
        patternCache.current.set(cacheKey, measures);
  
        // Update local state
        setSavedPatterns(prev => [...prev, {
          ...cleanedData,
          id: docRef.id,
          createdAt: new Date(),
          updatedAt: new Date()
        }]);
  
        alert(`Pattern "${actualPatternName}" saved to folder "${folderTrimmed}"!`);
      }
  
      // Track the manual save
      trackEvent("pattern_manual_save", {
        pattern_id: actualPatternName,
        folder: folderTrimmed,
        is_new: !existingPattern,
        measures_count: measures.length,
        time_signatures_used: getTimeSignaturesUsed(measures),
        features_used: getPatternFeatures(measures, muteRandomBeats)
      });
  
    } catch (err) {
      console.error("🔍 MANUAL SAVE DEBUG - Error saving pattern:", err);
      
      // Revert optimistic updates on error
      if (existingPattern) {
        setSavedPatterns(prev => {
          const reverted = [...prev];
          reverted[existingPatternIndex] = existingPattern;
          return reverted;
        });
      } else {
        setSavedPatterns(prev => prev.filter(p => !(p.name === actualPatternName && p.folder === folderTrimmed && !p.id)));
      }
      
      if (err.code === 'unavailable' || err.code === 'deadline-exceeded') {
        alert("Network error: Please check your connection and try again.");
      } else if (err.code === 'permission-denied') {
        alert("Permission error: Please log out and log back in.");
      } else {
        alert(`Failed to save pattern: ${err.message}`);
      }
    }
  }
  
  /**
   * Save As function that creates a new pattern with a different name/folder
   * @param {Object} params - All the parameters needed for saving
   * @returns {Promise} - Resolves when save is complete
   */
  export async function handleSaveAs({
    // User and authentication
    currentUser,
    
    // New pattern details
    newName,
    newFolder,
    
    // Pattern data
    patternName, // Original name for tracking
    tempo,
    useTempoChanges,
    enableAdditionalTempoChanges,
    tempoChanges,
    aTuning,
    muteRandomBeats,
    mutePercentage,
    loopMode,
    measures,
    startMeasureNumber,
    stressOn,
    selectedNoteValue,
    modulateOnLoop,
    modLoopInterval,
    modLoopDirection,
    rehearsalLetters,
    timeSignatureColors,
    
    // App state
    savedPatterns,
    setSavedPatterns,
    subscriptionTier,
    patternCache,
    
    // Dependencies
    removeUndefinedDeep,
  }) {
    if (!currentUser) {
      alert("Please log in to save patterns.");
      return;
    }

    console.log('🔍 SAVE AS DEBUG - Starting save as for:', newName, newFolder);

    // Build the pattern object
    const newPattern = {
      createdAt: serverTimestamp(), // Always new for Save As
      updatedAt: serverTimestamp(),
      name: newName,
      folder: newFolder,
      tempo,
      useTempoChanges: !!useTempoChanges,
      enableAdditionalTempoChanges: !!enableAdditionalTempoChanges,
      tempoChanges,
      aTuning,
      muteRandomBeats,
      mutePercentage,
      loopMode,
      measures,
      startMeasureNumber,
      stressOn,
      selectedNoteValue,
      modulateOnLoop: !!modulateOnLoop,
      modLoopInterval,
      modLoopDirection,
      rehearsalLetters,
      timeSignatureColors,
    };

    const cleanedData = removeUndefinedDeep(newPattern);
    const patternsRef = collection(db, "users", currentUser.uid, "patterns");

    try {
      console.log('🔍 SAVE AS DEBUG - Creating new pattern');

      // Check pattern limits
      let allowedCount;
      if (subscriptionTier === "pro") allowedCount = Infinity;
      else if (subscriptionTier === "basic") allowedCount = 10;
      else allowedCount = 3;

      if (savedPatterns.length >= allowedCount) {
        throw new Error(`Pattern limit reached (${allowedCount}). Please upgrade or delete patterns.`);
      }

      // Handle large patterns with chunking
      const { mainDoc, chunks, needsChunking } = splitPatternForStorage(cleanedData);

      // Always create new pattern (never update existing)
      const docRef = doc(patternsRef);

      if (needsChunking) {
        // Save main document
        await setDoc(docRef, mainDoc);
        
        // Save chunks as subcollection
        const batch = writeBatch(db);
        chunks.forEach((chunk, index) => {
          const chunkRef = doc(collection(docRef, 'chunks'), `chunk_${index}`);
          batch.set(chunkRef, chunk);
        });
        
        await batch.commit();
        console.log(`🔍 SAVE AS DEBUG - Created chunked pattern with ${chunks.length} chunks`);
      } else {
        // Regular save for small patterns
        await setDoc(docRef, mainDoc);
      }

      console.log('🔍 SAVE AS DEBUG - New pattern created with ID:', docRef.id);

      // Update cache immediately
      const cacheKey = `${newName}-${newFolder}`;
      console.log('🔍 SAVE AS DEBUG - Adding to cache with key:', cacheKey);
      patternCache.current.set(cacheKey, measures);

      // Optimistically add to local state immediately
      const newPatternWithId = {
        ...cleanedData, // Use original data for local state
        id: docRef.id,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      setSavedPatterns(prev => [...prev, newPatternWithId]);

      // Track analytics
      trackEvent("pattern_save_as", {
        original_name: patternName || "Untitled",
        new_name: newName,
        folder: newFolder,
        measures_count: measures.length,
        time_signatures_used: getTimeSignaturesUsed(measures),
        features_used: getPatternFeatures(measures, muteRandomBeats),
        is_chunked: needsChunking,
        chunk_count: chunks.length
      });

      const sizeInfo = needsChunking ? ` Pattern was split into ${chunks.length} chunks for storage.` : '';
      alert(`Pattern saved as "${newName}" in folder "${newFolder}"!${sizeInfo}`);

    } catch (err) {
      console.error("🔍 SAVE AS DEBUG - Error saving pattern:", err);
      
      // Remove the optimistically added pattern on error
      setSavedPatterns(prev => 
        prev.filter(p => !(p.name === newName && p.folder === newFolder && !p.id))
      );
      
      // Throw the error to be handled by the modal
      throw err;
    }
  }